import React, { useRef,  useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import { getSecurityLanes, createSecurityLane, updateSecurityLane, deleteSecurityLane, updateSecurityLaneStatus } from '../services/securityLaneService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

const Svg = ({ d, ...props }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round" {...props}>
    <path d={d} />
  </svg>
);

const SecurityLane = () => {
  const { refreshKey } = useRefresh();
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Custom Modal State
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Pagination & Filter States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    is_required: false,
    status: 1
  });
  const prevSearch = useRef(search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== search;
    prevSearch.current = search;

    const timer = setTimeout(() => {
      if (searchChanged && page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    }, searchChanged ? 500 : 0);

    return () => clearTimeout(timer);
  }, [page, limit, filterStatus, refreshKey, search]);


  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search || undefined,
        status: filterStatus === 'All' ? undefined : (filterStatus === 'Active' ? 1 : 2)
      };
      const res = await getSecurityLanes(params);
      setLanes(res?.data || []);
      setTotalPages(res?.meta?.totalPages || 1);
      setTotalRecords(res?.meta?.total || 0);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Failed to load security lanes");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (lane) => {
    setIsEditing(true);
    setCurrentId(lane.id);
    setFormData({
      name: lane.name || '',
      is_required: !!lane.is_required,
      status: Number(lane.status) || 1
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmDelete = (lane) => {
    setDeleteTarget(lane);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSecurityLane(deleteTarget.id);
      toast.success("Security lane deleted successfully");
      setShowConfirm(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleToggleStatus = async (lane) => {
    try {
      const newStatus = Number(lane.status) === 1 ? 2 : 1;
      await updateSecurityLaneStatus(lane.id, { status: newStatus });
      toast.success(`Security lane ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      return toast.warning("Please enter lane name");
    }

    setSubmitting(true);
    try {
      const response = isEditing 
        ? await updateSecurityLane(currentId, formData)
        : await createSecurityLane(formData);

      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(`Security lane ${isEditing ? 'updated' : 'created'} successfully`);
        resetForm();
        fetchData();
      } else {
        toast.error(response?.data?.message || "Operation failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', is_required: false, status: 1 });
    setIsEditing(false);
    setCurrentId(null);
    setShowForm(false);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading && lanes.length === 0) return <Spinner />;

  return (
    <div>
      <Topbar title="Security Lane Management" subtitle="Manage specific types of security lanes available at checkpoints" />
      
      {/* CUSTOM CONFIRMATION MODAL */}
      {showConfirm && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ background: 'white', padding: 32, borderRadius: 20, width: '100%', maxWidth: 400, textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 64, height: 64, background: '#FFF5F5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--dark)', marginBottom: 10 }}>Confirm Deletion</h3>
            <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 24, lineHeight: 1.5 }}>
              Are you sure you want to delete <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{deleteTarget?.name}</span>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1, background: '#F87171', borderColor: '#F87171' }} onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="content">
        <div className="screen-label">5.4 — Security Lane Management</div>

        <div className="card">
          {/* FORM SECTION */}
          {(showForm || isEditing) && (
            <div style={{ borderBottom: '2px solid #F0F4F8', padding: '24px 26px', background: '#F8FAFC', borderRadius: '20px 20px 0 0' }}>
              <div className="screen-label" style={{ marginBottom: 18, fontSize: 13, color: 'var(--blue)', fontWeight: 700 }}>{isEditing ? 'Edit Security Lane' : 'Create New Security Lane'}</div>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group full">
                    <label className="form-label">Lane Name <span className="req">*</span></label>
                    <input 
                      className="form-input" 
                      placeholder='e.g. "TSA PreCheck" or "CLEAR"' 
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Requirement Type</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                      <input 
                        type="checkbox" 
                        id="is_required" 
                        checked={formData.is_required}
                        onChange={e => setFormData({...formData, is_required: e.target.checked})}
                        style={{ width: 18, height: 18, accentColor: 'var(--blue)' }}
                      />
                      <label htmlFor="is_required" style={{ fontSize: 13, color: 'var(--dark)', fontWeight: 500, cursor: 'pointer' }}>Mark as Required</label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status <span className="req">*</span></label>
                    <div className="toggle-row" style={{ padding: '9px 12px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>Active in app</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontSize: 11, color: 'var(--grey)' }}>Inactive</span>
                        <button 
                          type="button"
                          className={`toggle-switch ${Number(formData.status) === 1 ? 'on' : 'off'}`} 
                          onClick={() => setFormData({...formData, status: Number(formData.status) === 1 ? 2 : 1})}
                        />
                        <span style={{ fontSize: 11, fontWeight: 700, color: Number(formData.status) === 1 ? 'var(--green)' : 'var(--grey)' }}>Active</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-footer" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 20 }}>
                  <button type="button" className="btn btn-outline" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/></svg>
                    {submitting ? 'Saving...' : isEditing ? 'Update Lane' : 'Save Lane'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="toolbar">
            <div className="search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input 
                className="search-input" 
                placeholder="Search security lanes..." 
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select 
              className="filter-select" 
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            {!showForm && (
              <button className="btn btn-primary" onClick={() => setShowForm(true)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>Add Lane</button>
            )}
          </div>

          <table className="tbl">
            <thead>
              <tr>
                <th>Lane Name</th>
                <th>Requirement</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px' }}><Spinner /></td></tr>
              ) : lanes.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>No security lanes found</td></tr>
              ) : (
                lanes.map((lane) => (
                  <tr key={lane.id}>
                    <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{lane.name}</td>
                    <td>
                      <span className={`tag ${lane.is_required ? 'tag-required' : 'tag-optional'}`} style={{ 
                        background: lane.is_required ? '#FFF0F0' : '#F0F9FF',
                        color: lane.is_required ? '#E53E3E' : '#0284C7',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '700'
                      }}>
                        {lane.is_required ? 'REQUIRED' : 'OPTIONAL'}
                      </span>
                    </td>
                    <td><span className={`badge badge-${Number(lane.status) === 1 ? 'active' : 'inactive'}`}>{Number(lane.status) === 1 ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="action-btns">
                        <button className="action-btn" title={Number(lane.status) === 1 ? "Deactivate" : "Activate"} onClick={() => handleToggleStatus(lane)}>
                          {Number(lane.status) === 1 ? (
                            <Svg d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          ) : (
                            <Svg d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          )}
                        </button>
                        <button className="action-btn" onClick={() => handleEdit(lane)}><Svg d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></button>
                        <button className="action-btn danger" onClick={() => confirmDelete(lane)}><Svg d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          {lanes.length > 0 && (
            <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderTop: '1px solid #F0F4F8' }}>
              <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((page - 1) * limit) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> entries
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button 
                  className="btn btn-outline" 
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', gap: 4 }}>
                  {renderPagination().map((p, i) => (
                    <button 
                      key={i}
                      className={`btn ${page === p ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => typeof p === 'number' && setPage(p)}
                      style={{ 
                        padding: '6px 12px', 
                        fontSize: 12, 
                        minWidth: 36,
                        cursor: typeof p === 'number' ? 'pointer' : 'default',
                        border: typeof p === 'number' ? '' : 'none',
                        background: typeof p === 'number' ? '' : 'transparent'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button 
                  className="btn btn-outline" 
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityLane;
